/**
 * The dashboard toolbar's **Person** and **Filter** panes (`specs/M10-D1-dashboards.md` §2.3,
 * card **P-184**).
 *
 * The two buttons are measured — Person 87 × 32, Filter 95 × 32 — and the `dashboards.filter`
 * column, `updateDashboard(filter:)` and its round trip were all built and proven; what was
 * missing was any way at all to write one, so no dashboard in this product could be filtered.
 *
 * **What is ours and what is monday's.** The buttons' geometry is monday's. The panes' contents
 * are not: `docs/open-questions.md` **q537** records that monday's dashboard filter pane was
 * never captured, and that a canvas over several boards could plausibly offer the union, the
 * intersection or a per-board list. This takes the narrowest honest reading — the columns the
 * canvas's own boards agree on, through the same `ItemsFilter` tree the board view saves, so a
 * rule written here means exactly what the same rule means on a board (`filter-compiler.ts` is
 * the one compiler). The api half applies it in `services/aggregate.ts`, and a board that cannot
 * answer every rule contributes nothing rather than everything.
 */
import { useEffect, useRef } from 'react';
import { useOverlayDismiss } from '@elizmonday/ui';
import type { CanvasColumnOption, CanvasSettingsOptions } from './types';

/** The people-like column types a Person filter writes against (the board pane's own set). */
const PEOPLE_LIKE = new Set(['people', 'person', 'team', 'item_assignees']);

interface FilterRule {
  columnId: string;
  operator: string;
  compareValues: unknown[];
}

interface FilterTree {
  operator: 'and' | 'or';
  rules: FilterRule[];
  groups: FilterTree[];
}

const EMPTY_TREE: FilterTree = { operator: 'and', rules: [], groups: [] };

export function asFilterTree(value: unknown): FilterTree {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return EMPTY_TREE;
  const raw = value as Record<string, unknown>;
  const rules = Array.isArray(raw['rules']) ? (raw['rules'] as Record<string, unknown>[]) : [];
  return {
    operator: raw['operator'] === 'or' ? 'or' : 'and',
    rules: rules.flatMap((rule) =>
      typeof rule['columnId'] === 'string' && typeof rule['operator'] === 'string'
        ? [
            {
              columnId: rule['columnId'],
              operator: rule['operator'],
              compareValues: Array.isArray(rule['compareValues'])
                ? rule['compareValues']
                : rule['compareValue'] === undefined || rule['compareValue'] === null
                  ? []
                  : [rule['compareValue']],
            },
          ]
        : [],
    ),
    groups: [],
  };
}

/** `null` when nothing is filtered, otherwise the short label the toolbar button appends. */
export function describeCanvasFilter(value: unknown): string | null {
  const tree = asFilterTree(value);
  if (tree.rules.length === 0) return null;
  return tree.rules.length === 1 ? '1 rule' : `${tree.rules.length} rules`;
}

/** `{}`/`{rules: []}` is stored as `null`: an empty tree is "no filter", not "a filter of none". */
function commit(tree: FilterTree, onChange: (next: unknown) => void): void {
  onChange(tree.rules.length === 0 ? null : tree);
}

/**
 * Closes the pane on an outside mousedown and on `Escape` — the P-013 contract. `Escape` goes
 * through the one overlay-dismiss hook the whole product shares rather than a fourth copy of the
 * same `keydown` listener; the outside click is the half that hook deliberately does not own.
 */
function useDismiss(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useOverlayDismiss({ onClose });
  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (ref.current !== null && !ref.current.contains(event.target as Node)) onClose();
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [onClose]);
  return ref;
}

export interface CanvasPaneProps {
  options: CanvasSettingsOptions | undefined;
  value: unknown;
  onChange(next: unknown): void;
  onClose(): void;
  /**
   * The id the pane's root renders, declared so the **call site** may state it too: the coverage
   * ledger reads literal `data-testid` attributes off JSX, and a component invocation carrying
   * only handlers was an element nothing could ever name (P-189).
   */
  'data-testid'?: string;
}

/* ── Person ───────────────────────────────────────────────────────────────── */

/**
 * *Person* writes the same rule the board's own person filter writes — `any_of` over every
 * people-like column — which is why it is a rule set and not a second kind of filter.
 * The people it can offer are the ones the canvas's boards declare as columns; the person **ids**
 * a canvas could pick from need an account read this surface does not have (q537), so until one
 * capture settles the pane, it offers the honest half: *assigned to anybody* per column.
 */
export function CanvasPersonPane({ options, value, onChange, onClose }: CanvasPaneProps) {
  const ref = useDismiss(onClose);
  const tree = asFilterTree(value);
  const columns = (options?.dimensionColumns ?? []).filter((column) => PEOPLE_LIKE.has(column.type));
  const chosen = new Set(tree.rules.filter((rule) => rule.operator === 'is_not_empty').map((rule) => rule.columnId));

  const toggle = (column: CanvasColumnOption, on: boolean) => {
    const rules = tree.rules.filter((rule) => !(rule.columnId === column.id && rule.operator === 'is_not_empty'));
    commit(
      {
        ...tree,
        rules: on ? [...rules, { columnId: column.id, operator: 'is_not_empty', compareValues: [] }] : rules,
      },
      onChange,
    );
  };

  return (
    <div ref={ref} className="ez-widget-pane" data-testid="widget-person-pane" role="dialog" aria-label="Filter by person">
      <p className="ez-widget-pane__hint">Show only items that have somebody in:</p>
      {columns.length === 0 ? (
        <p className="ez-widget-pane__hint" data-testid="widget-person-empty">
          No board on this dashboard has a people column.
        </p>
      ) : (
        columns.map((column) => (
          <label key={`${column.boardId}:${column.id}`} className="ez-widget-pane__check">
            <input
              type="checkbox"
              checked={chosen.has(column.id)}
              data-testid={`widget-person-${column.id}`}
              onChange={(event) => toggle(column, event.target.checked)}
            />
            <span>{column.title}</span>
          </label>
        ))
      )}
    </div>
  );
}

/* ── Filter ───────────────────────────────────────────────────────────────── */

/**
 * One rule per row: a column of the canvas's boards, an operator and a value. The operators are
 * deliberately the three every column type in `filter-compiler.ts` supports — `is` / `is not` and
 * the two empty tests — rather than the board's full per-type condition list, because a dashboard
 * rule is compiled against **several** boards and an operator one of them refuses is a 400 for
 * the whole canvas.
 */
export function CanvasFilterPane({ options, value, onChange, onClose }: CanvasPaneProps) {
  const ref = useDismiss(onClose);
  const tree = asFilterTree(value);
  const columns = options?.dimensionColumns ?? [];

  const replace = (index: number, patch: Partial<FilterRule>) => {
    commit(
      { ...tree, rules: tree.rules.map((rule, at) => (at === index ? { ...rule, ...patch } : rule)) },
      onChange,
    );
  };

  return (
    <div ref={ref} className="ez-widget-pane" data-testid="widget-filter-pane" role="dialog" aria-label="Filter this dashboard">
      {tree.rules.length === 0 ? <p className="ez-widget-pane__hint">This dashboard shows every item.</p> : null}
      {tree.rules.map((rule, index) => (
        <div key={`${rule.columnId}-${index}`} className="ez-widget-pane__rule">
          <select
            value={rule.columnId}
            data-testid={`widget-filter-column-${index}`}
            onChange={(event) => replace(index, { columnId: event.target.value })}
          >
            {columns.map((column) => (
              <option key={`${column.boardId}:${column.id}`} value={column.id}>
                {column.title}
              </option>
            ))}
          </select>
          <select
            value={rule.operator}
            data-testid={`widget-filter-operator-${index}`}
            onChange={(event) => replace(index, { operator: event.target.value })}
          >
            <option value="any_of">is</option>
            <option value="not_any_of">is not</option>
            <option value="is_empty">is empty</option>
            <option value="is_not_empty">is not empty</option>
          </select>
          <input
            type="text"
            value={rule.compareValues.map(String).join(', ')}
            placeholder="value"
            disabled={rule.operator === 'is_empty' || rule.operator === 'is_not_empty'}
            data-testid={`widget-filter-value-${index}`}
            onChange={(event) =>
              replace(index, {
                compareValues: event.target.value
                  .split(',')
                  .map((part) => part.trim())
                  .filter((part) => part !== ''),
              })
            }
          />
          <button
            type="button"
            aria-label="Remove this rule"
            data-testid={`widget-filter-remove-${index}`}
            onClick={() => commit({ ...tree, rules: tree.rules.filter((_, at) => at !== index) }, onChange)}
          >
            ✕
          </button>
        </div>
      ))}
      <div className="ez-widget-pane__actions">
        <button
          type="button"
          data-testid="widget-filter-add"
          disabled={columns.length === 0}
          onClick={() =>
            commit(
              {
                ...tree,
                rules: [
                  ...tree.rules,
                  { columnId: columns[0]?.id ?? '', operator: 'is_not_empty', compareValues: [] },
                ],
              },
              onChange,
            )
          }
        >
          + New filter
        </button>
        <button type="button" data-testid="widget-filter-clear" disabled={tree.rules.length === 0} onClick={() => onChange(null)}>
          Clear all
        </button>
      </div>
    </div>
  );
}
